import { v4 } from "uuid";

export interface RedirectParam {
  id: string;
  name: string;
  /** May contain recall tokens ({{blockId}}, @hiddenField, @score) resolved at redirect time. */
  value: string;
}

/**
 * Redirect screen params are persisted as a JSON array on the block's
 * `secondaryContent` string field (REDIRECT_SCREEN doesn't otherwise use it).
 * Parse defensively — legacy/blank values just yield no params.
 */
export const parseRedirectParams = (raw: string | null | undefined): RedirectParam[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((p): p is RedirectParam => !!p && typeof p === 'object' && typeof p.name === 'string')
        .map(p => ({ id: p.id || v4(), name: p.name, value: typeof p.value === 'string' ? p.value : '' }));
    }
  } catch {
    // Not JSON — treat as no params.
  }
  return [];
};

/** Prefix a bare host (`example.com`) with https:// so it isn't treated as a relative path. */
export const normalizeRedirectUrl = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return '';
  // Leave any explicit scheme (https:, mailto:, tel:, etc.) untouched.
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/**
 * Append already-resolved query params to a base URL. Uses the URL API when the
 * base parses cleanly (preserving any existing query string), and falls back to
 * manual string concatenation for non-standard schemes.
 */
export const buildRedirectUrl = (
  baseUrl: string,
  params: { name: string; value: string }[],
): string => {
  const normalized = normalizeRedirectUrl(baseUrl);
  const usableParams = params.filter(p => p.name.trim().length > 0);
  if (!usableParams.length) return normalized;

  try {
    const url = new URL(normalized);
    usableParams.forEach(({ name, value }) => url.searchParams.append(name.trim(), value));
    return url.toString();
  } catch {
    const query = usableParams
      .map(({ name, value }) => `${encodeURIComponent(name.trim())}=${encodeURIComponent(value)}`)
      .join('&');
    return `${normalized}${normalized.includes('?') ? '&' : '?'}${query}`;
  }
};
